/**
 * RunTaskModal.tsx — "Run this file as a task" configuration dialog.
 *
 * Opened from a file tab's context menu → "Run…". Lets the user configure how
 * a file is run as a backend task + optionally save the configuration for
 * autostart. Today the backend tracks two task kinds (model + notebook); this
 * modal is the future home for arbitrary "run this .py / .ipynb as a managed
 * task" configuration. Until that backend surface lands, the modal records the
 * intent locally (the Autostart checkbox + run kind) and the actual run is a
 * no-op stub that surfaces "backend support coming soon" — this keeps the UX
 * shippable without a half-wired backend.
 *
 * The modal is mounted once at the App shell level and driven by the
 * `code:run-file` window event (dispatched by FileTabs' context menu), so the
 * tab strip doesn't need a callback prop threaded through every layer.
 */
import { useEffect, useState } from 'react';
import { Modal } from '../panels/Modal';
import { Play } from '@phosphor-icons/react';

interface RunConfig {
  path: string;
  kind: 'notebook' | 'script';
  autostart: boolean;
}

interface RunTaskModalProps {
  /** The path to run (from the `code:run-file` event), or null when closed. */
  path: string | null;
  onClose: () => void;
  /** Persist a saved task config (future: backend writes project.json). Today
   *  this is a stub that logs — the backend task surface is assessed + planned
   *  separately. */
  onSave?: (config: RunConfig) => void;
}

export function RunTaskModal({ path, onClose, onSave }: RunTaskModalProps): JSX.Element | null {
  const [autostart, setAutostart] = useState(false);
  const [kind, setKind] = useState<'notebook' | 'script'>('script');

  // Derive the run kind from the extension + reset state when the target changes.
  useEffect(() => {
    if (!path) return;
    setKind(path.endsWith('.ipynb') ? 'notebook' : 'script');
    setAutostart(false);
  }, [path]);

  if (!path) return null;

  const name = path.split('/').pop() ?? path;

  const handleSave = (): void => {
    onSave?.({ path, kind, autostart });
    onClose();
  };

  return (
    <Modal title={`Run ${name}`} onClose={onClose}>
      <div className="run-task-body">
        <p className="run-task-path">
          <code>{path}</code>
        </p>
        <p className="run-task-hint">
          Configure how this file runs as a managed task. The backend currently
          tracks the model subprocess + Jupyter notebook server; running an
          arbitrary file as a task is planned (see the Code-tab roadmap).
        </p>

        <fieldset className="run-task-fieldset">
          <legend>Run as</legend>
          <label className="run-task-radio">
            <input
              type="radio"
              name="run-kind"
              checked={kind === 'script'}
              onChange={() => setKind('script')}
            />
            <span>
              <strong>Script</strong>
              <span className="run-task-radio-hint">
                {kind === 'script' ? 'python module + args' : ''}
              </span>
            </span>
          </label>
          <label className="run-task-radio">
            <input
              type="radio"
              name="run-kind"
              checked={kind === 'notebook'}
              onChange={() => setKind('notebook')}
            />
            <span>
              <strong>Notebook</strong>
              <span className="run-task-radio-hint">
                {kind === 'notebook' ? 'Jupyter kernel + cells' : ''}
              </span>
            </span>
          </label>
        </fieldset>

        <label className="run-task-check">
          <input
            type="checkbox"
            checked={autostart}
            onChange={(e) => setAutostart(e.target.checked)}
          />
          <span>
            <strong>Autostart</strong>
            <span className="run-task-radio-hint">
              Run this task when the project opens.
            </span>
          </span>
        </label>
      </div>
      <div className="run-task-footer">
        <button type="button" className="btn" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn btn-start" onClick={handleSave}>
          <Play aria-hidden /> Save & Run
        </button>
      </div>
    </Modal>
  );
}

/** Convenience hook: mounts the modal + listens for the `code:run-file` event
 *  dispatched by FileTabs' context menu. Returns [path, closer]. App.tsx uses
 *  this so it doesn't have to thread a callback through the Code panel. */
export function useRunTaskModal(): {
  path: string | null;
  close: () => void;
} {
  const [path, setPath] = useState<string | null>(null);
  useEffect(() => {
    const onRun = (e: Event): void => {
      const detail = (e as CustomEvent<{ path: string }>).detail;
      if (detail?.path) setPath(detail.path);
    };
    window.addEventListener('code:run-file', onRun);
    return () => window.removeEventListener('code:run-file', onRun);
  }, []);
  return { path, close: () => setPath(null) };
}
